import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, PackageMinus, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArticlePicker } from "@/components/ArticlePicker";
import { IssuesAlert, TH } from "@/components/common";
import { CameraButton } from "@/components/CameraScanner";
import { api, apiError, apiIssues, fmtQty, lookupCode } from "@/lib/api";
import { scanFeedback } from "@/lib/feedback";

const TYPES = [
  { id: "entree", label: "Entrée", icon: ArrowDownToLine, cls: "data-[on=true]:border-emerald-500 data-[on=true]:bg-emerald-50 data-[on=true]:text-emerald-800" },
  { id: "sortie", label: "Sortie", icon: ArrowUpFromLine, cls: "data-[on=true]:border-slate-700 data-[on=true]:bg-slate-100 data-[on=true]:text-slate-900" },
  { id: "ajustement", label: "Ajustement", icon: ClipboardCheck, cls: "data-[on=true]:border-blue-500 data-[on=true]:bg-blue-50 data-[on=true]:text-blue-800" },
  { id: "sortie_kit", label: "Sortie kit", icon: PackageMinus, cls: "data-[on=true]:border-purple-500 data-[on=true]:bg-purple-50 data-[on=true]:text-purple-800" },
];

export function KitNeeds({ kit, qty }) {
  const n = Number(qty) || 0;
  return (
    <div className="overflow-x-auto rounded border" data-testid="kit-needs-table">
      <Table>
        <TableHeader><TableRow><TableHead className={TH}>Composant</TableHead><TableHead className={`${TH} text-right`}>Besoin</TableHead><TableHead className={`${TH} text-right`}>Disponible</TableHead><TableHead className={`${TH} text-right`}>Manque</TableHead></TableRow></TableHeader>
        <TableBody>
          {kit.components.map((c) => {
            const need = c.qty * n, miss = Math.max(0, need - c.stock);
            return (
              <TableRow key={c.code} className={miss > 0 ? "bg-rose-50" : ""} data-testid={`kit-needs-row-${c.code}`}>
                <TableCell><span className="font-mono font-semibold">{c.code}</span> — {c.designation}</TableCell>
                <TableCell className="text-right font-mono">{fmtQty(need)}</TableCell>
                <TableCell className="text-right font-mono">{fmtQty(c.stock)}</TableCell>
                <TableCell className={`text-right font-mono font-semibold ${miss > 0 ? "text-rose-600" : "text-emerald-600"}`}>{miss > 0 ? fmtQty(miss) : "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function MovementForm({ initialType, initialCode, onDone }) {
  const [type, setType] = useState(TYPES.some((t) => t.id === initialType) ? initialType : "entree");
  const [article, setArticle] = useState(null);
  const [kits, setKits] = useState([]);
  const [kitCode, setKitCode] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [issues, setIssues] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadKits = () => api.get("/kits").then((r) => setKits(r.data));
  useEffect(() => { loadKits(); }, []);
  useEffect(() => {
    if (!initialCode) return;
    if (initialType === "sortie_kit") setKitCode(initialCode);
    else api.get(`/articles/${encodeURIComponent(initialCode)}`).then((r) => setArticle(r.data)).catch(() => {});
  }, [initialCode, initialType]);

  const isKit = type === "sortie_kit";
  const kit = useMemo(() => kits.find((k) => k.code === kitCode), [kits, kitCode]);
  const n = Number(qty);
  const after = article && qty !== "" ? (type === "entree" ? article.stock + n : type === "sortie" ? article.stock - n : n) : null;

  const fromCamera = async (code) => {
    setIssues([]);
    try {
      const data = await lookupCode(code);
      if (data.kind === "none") { scanFeedback(false, `Code inconnu : ${code}`); return toast.error(`Code inconnu : ${code}`); }
      if (data.kind === "kit") { setType("sortie_kit"); setKitCode(data.item.code); }
      else { if (type === "sortie_kit") setType("sortie"); setArticle(data.item); }
      toast.success(`${data.item.code} — ${data.item.designation}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setIssues([]);
    const code = isKit ? kitCode : article?.code;
    if (!code) return toast.error(isKit ? "Choisissez un kit." : "Choisissez un article.");
    setBusy(true);
    try {
      const { data } = await api.post("/movements", { type, code, quantity: n, note: note || null });
      toast.success(data.applied[0]?.replace(/^1\. /, "") || "Mouvement enregistré");
      setQty(""); setNote("");
      if (article) setArticle((await api.get(`/articles/${encodeURIComponent(article.code)}`)).data);
      loadKits();
      onDone?.();
    } catch (err) {
      const list = apiIssues(err);
      setIssues(list.length ? list : [apiError(err)]);
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-6 xl:grid-cols-3" data-testid="movement-form">
      <div className="space-y-6 rounded-md border bg-white p-6 shadow-sm xl:col-span-2">
        <div>
          <Label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Type de mouvement</Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {TYPES.map(({ id, label, icon: Icon, cls }) => (
              <button type="button" key={id} data-on={type === id} onClick={() => { setType(id); setIssues([]); }} data-testid={`movement-type-${id}`}
                className={`flex h-20 flex-col items-center justify-center gap-1.5 rounded-md border-2 border-slate-200 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-400 ${cls}`}>
                <Icon className="h-5 w-5" />{label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{isKit ? "Kit" : "Article"}</Label>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              {isKit ? (
                <Select value={kitCode} onValueChange={(v) => { setKitCode(v); setIssues([]); }}>
                  <SelectTrigger className="h-12" data-testid="movement-kit-select"><SelectValue placeholder="Choisir un kit…" /></SelectTrigger>
                  <SelectContent>{kits.map((k) => <SelectItem key={k.code} value={k.code} data-testid={`movement-kit-option-${k.code}`}>{k.code} — {k.designation} ({k.buildable} réalisables)</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <ArticlePicker value={article} onChange={(a) => { setArticle(a); setIssues([]); }} testid="movement-article-picker" />
              )}
            </div>
            <CameraButton iconOnly onDetected={fromCamera} testid="movement-camera-button" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{type === "ajustement" ? "Nouvelle quantité en stock (inventaire)" : isKit ? "Nombre de kits" : "Quantité"}</Label>
            <Input type="number" required min={type === "ajustement" ? 0 : isKit ? 1 : 0.001} step={isKit ? 1 : "any"} value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 font-mono text-lg" data-testid="movement-quantity-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Note (optionnelle)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="min-h-12" placeholder="Bon de livraison, chantier…" data-testid="movement-note-input" />
          </div>
        </div>
        <IssuesAlert title="Mouvement bloqué" issues={issues} testid="movement-issues" />
        <Button type="submit" disabled={busy} className="h-14 w-full gap-2 text-base" data-testid="movement-submit-button"><CheckCircle2 className="h-5 w-5" /> {busy ? "Enregistrement…" : "Valider le mouvement"}</Button>
      </div>
      <aside className="space-y-4 rounded-md border bg-white p-6 shadow-sm" data-testid="movement-preview">
        <h3 className="font-display text-xl font-bold uppercase">Aperçu</h3>
        {!isKit && article && (
          <div className="space-y-3">
            <div className="text-sm"><span className="font-mono font-semibold">{article.code}</span> — {article.designation}</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded bg-slate-50 p-3"><div className="text-xs uppercase text-slate-500">Stock actuel</div><div className="font-mono text-2xl font-bold" data-testid="preview-current-stock">{fmtQty(article.stock)}</div></div>
              <div className={`rounded p-3 ${after !== null && after < 0 ? "bg-rose-50 text-rose-700" : "bg-slate-50"}`}><div className="text-xs uppercase text-slate-500">Après</div><div className="font-mono text-2xl font-bold" data-testid="preview-after-stock">{after === null ? "—" : fmtQty(after)}</div></div>
            </div>
            {after !== null && after < 0 && <p className="text-sm text-rose-600" data-testid="preview-negative-warning">Stock négatif interdit : sortie impossible.</p>}
            <div className="text-xs text-slate-500">Unité : {article.unite} · Seuil : {fmtQty(article.seuil_min)}</div>
          </div>
        )}
        {isKit && kit && <><div className="text-sm">{kit.buildable} kit(s) réalisable(s), limitant <span className="font-mono font-semibold">{kit.limiting}</span></div><KitNeeds kit={kit} qty={qty} /></>}
        {((!isKit && !article) || (isKit && !kit)) && <p className="text-sm text-slate-500">Sélectionnez {isKit ? "un kit" : "un article"} pour voir l'impact sur le stock.</p>}
      </aside>
    </form>
  );
}
